import React from "react";

const C = {
  primary:      "#5E0E73",
  primaryLight: "#6E1A85",
  cream:        "#E8E1D6",
  gold:         "#C9A84C",
  goldLight:    "#E8D5A0",
  dark:         "#1A0030",
};

const STEPS = [
  {
    step: 1,
    icon: "📍",
    title: "Location & Date",
    desc: "Choose your preferred Carnival Castle venue and pick your celebration date",
  },
  {
    step: 2,
    icon: "🎭",
    title: "Décor & Time Slot",
    desc: "Select your private theatre, decor theme, and book your preferred time slot",
  },
  {
    step: 3,
    icon: "🎊",
    title: "Choose Occasion",
    desc: "Tell us what you're celebrating — Birthday, Anniversary, Proposal & more",
  },
  {
    step: 4,
    icon: "🎂",
    title: "Cake & Add-ons",
    desc: "Personalise with a custom cake, rose bouquets, fog machine, and extra surprises",
  },
  {
    step: 5,
    icon: "💳",
    title: "Payment",
    desc: "Confirm your booking and pay securely via Razorpay — done in under 2 minutes",
  },
];

export default function BookingProcess() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;0,900;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');

        @keyframes bp-float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-9px)} }
        @keyframes bp-fadeUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes bp-pulse  { 0%,100%{opacity:.35;transform:scale(.88)} 50%{opacity:.65;transform:scale(1.08)} }
        @keyframes bp-shimmer{ 0%{background-position:200% center} 100%{background-position:-200% center} }

        /* ── section ── */
        .bp-sec {
          padding: 96px 0 104px;
          background: ${C.cream};
          position: relative;
          overflow: hidden;
          font-family: 'DM Sans', sans-serif;
        }

        /* very subtle noise */
        .bp-sec::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.022'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 0;
        }

        /* decorative orbs */
        .bp-orb {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          filter: blur(72px);
          z-index: 0;
          animation: bp-pulse 7s ease-in-out infinite;
        }

        .bp-container {
          max-width: 1160px;
          margin: 0 auto;
          padding: 0 28px;
          position: relative;
          z-index: 1;
        }

        /* ── header ── */
        .bp-header {
          text-align: center;
          margin-bottom: 76px;
          animation: bp-fadeUp .75s cubic-bezier(.16,1,.3,1) both;
        }

        .bp-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 3.5px;
          text-transform: uppercase;
          color: ${C.gold};
          margin-bottom: 16px;
        }
        .bp-eyebrow-line {
          width: 28px; height: 1px;
          background: ${C.gold};
          opacity: .55;
        }

        .bp-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(34px, 5vw, 54px);
          font-weight: 700;
          color: ${C.dark};
          margin: 0 0 12px;
          line-height: 1.1;
          letter-spacing: -.5px;
        }
        .bp-title .gold {
          color: ${C.gold};
          font-style: italic;
        }

        .bp-sub {
          font-size: 15px;
          color: rgba(26,0,48,.52);
          max-width: 480px;
          margin: 0 auto;
          line-height: 1.65;
        }

        /* ── main row ── */
        .bp-row {
          display: flex;
          align-items: flex-start;
          justify-content: center;
          position: relative;
          gap: 0;
        }

        /* gold connector spine — sits at circle centre height (39px) */
        .bp-spine {
          position: absolute;
          top: 39px;
          left: 0; right: 0;
          height: 0;
          pointer-events: none;
          z-index: 0;
        }
        .bp-spine-line {
          position: absolute;
          top: 0;
          left: 10%;   /* first circle centre */
          right: 10%;  /* last circle centre */
          height: 2px;
          background: linear-gradient(
            90deg,
            transparent,
            ${C.gold}55 12%,
            ${C.gold} 30%,
            ${C.gold} 70%,
            ${C.gold}55 88%,
            transparent
          );
          border-radius: 2px;
        }

        /* animated gold shimmer on the line */
        .bp-spine-shimmer {
          position: absolute;
          top: 0;
          left: 10%; right: 10%;
          height: 2px;
          background: linear-gradient(
            90deg,
            transparent 0%,
            ${C.goldLight}ee 50%,
            transparent 100%
          );
          background-size: 200% 100%;
          animation: bp-shimmer 3.5s linear infinite;
          border-radius: 2px;
          opacity: .7;
        }

        /* ── single step ── */
        .bp-step {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 0 14px;
          position: relative;
          z-index: 1;
          cursor: default;
        }

        /* circle wrapper — leaves room for float anim */
        .bp-circle-wrap {
          position: relative;
          margin-bottom: 24px;
          width: 78px;
          height: 78px;
        }

        /* outer ring (hover reveal) */
        .bp-ring {
          position: absolute;
          inset: -9px;
          border-radius: 50%;
          border: 1.5px solid ${C.gold};
          opacity: 0;
          transform: scale(.82);
          transition: opacity .35s, transform .4s cubic-bezier(.25,1,.5,1);
          pointer-events: none;
        }
        .bp-step:hover .bp-ring {
          opacity: .5;
          transform: scale(1);
        }

        /* main circle */
        .bp-circle {
          width: 78px;
          height: 78px;
          border-radius: 50%;
          background: linear-gradient(145deg, ${C.primary} 0%, ${C.primaryLight} 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          position: relative;
          box-shadow:
            0 10px 28px rgba(94,14,115,.26),
            0 0 0 4px rgba(201,168,76,.16);
          transition:
            transform .4s cubic-bezier(.25,1,.5,1),
            box-shadow .4s;
        }

        /* staggered float */
        .bp-step:nth-child(1) .bp-circle { animation: bp-float 5.0s ease-in-out 0.0s infinite; }
        .bp-step:nth-child(2) .bp-circle { animation: bp-float 5.0s ease-in-out 0.5s infinite; }
        .bp-step:nth-child(3) .bp-circle { animation: bp-float 5.0s ease-in-out 1.0s infinite; }
        .bp-step:nth-child(4) .bp-circle { animation: bp-float 5.0s ease-in-out 1.5s infinite; }
        .bp-step:nth-child(5) .bp-circle { animation: bp-float 5.0s ease-in-out 2.0s infinite; }

        .bp-step:hover .bp-circle {
          transform: translateY(-7px) scale(1.06);
          box-shadow:
            0 20px 44px rgba(94,14,115,.32),
            0 0 0 5px rgba(201,168,76,.28);
        }

        /* step number badge */
        .bp-num {
          position: absolute;
          top: -5px;
          right: -5px;
          width: 23px;
          height: 23px;
          border-radius: 50%;
          background: linear-gradient(135deg, #AA7A10, ${C.gold});
          color: ${C.dark};
          font-size: 10px;
          font-weight: 900;
          font-family: 'DM Sans', sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2.5px solid ${C.cream};
          letter-spacing: 0;
          box-shadow: 0 3px 8px rgba(201,168,76,.35);
        }

        /* text */
        .bp-step-title {
          font-family: 'Playfair Display', serif;
          font-size: 16px;
          font-weight: 700;
          color: ${C.primary};
          margin: 0 0 8px;
          transition: color .25s;
          line-height: 1.25;
        }
        .bp-step:hover .bp-step-title {
          color: ${C.primaryLight};
        }

        .bp-step-desc {
          font-size: 12.5px;
          color: rgba(26,0,48,.55);
          line-height: 1.65;
          max-width: 155px;
          margin: 0 auto;
        }

        /* ── responsive: tablet (3+2) ── */
        @media (max-width: 900px) {
          .bp-row { flex-wrap: wrap; gap: 44px 0; }
          .bp-spine { display: none; }
          .bp-step { flex: 0 0 33.33%; min-width: 0; }
        }

        /* ── responsive: mobile ── */
        @media (max-width: 600px) {
          .bp-sec { padding: 64px 0 72px; }
          .bp-header { margin-bottom: 48px; }
          .bp-row { flex-direction: column; align-items: stretch; gap: 0; }
          .bp-step {
            flex-direction: row;
            text-align: left;
            align-items: flex-start;
            gap: 18px;
            padding: 0 0 36px;
            flex: none;
          }
          .bp-circle-wrap { margin-bottom: 0; flex-shrink: 0; }
          .bp-circle { animation: none !important; }
          .bp-step-desc { max-width: none; }

          /* vertical dot connector */
          .bp-step:not(:last-child)::after {
            content: '';
            position: absolute;
            left: 38px;
            top: 82px;
            width: 2px;
            height: calc(100% - 82px);
            background: linear-gradient(to bottom, ${C.gold}66, ${C.gold}11);
            border-radius: 1px;
          }
        }
      `}</style>

      <section className="bp-sec">

        {/* decorative orbs */}
        <div className="bp-orb" style={{ width:360, height:360, background:`radial-gradient(circle,rgba(94,14,115,.11) 0%,transparent 70%)`, top:-80, left:-60 }} />
        <div className="bp-orb" style={{ width:280, height:280, background:`radial-gradient(circle,rgba(201,168,76,.1) 0%,transparent 70%)`, bottom:-60, right:-40, animationDelay:"3.5s" }} />

        <div className="bp-container">

          {/* Header */}
          <div className="bp-header">
            <div className="bp-eyebrow">
              <div className="bp-eyebrow-line" />
              How It Works
              <div className="bp-eyebrow-line" />
            </div>
            <h2 className="bp-title">
              Your <span className="gold">Booking</span> Journey
            </h2>
            <p className="bp-sub">
              Five simple steps to your perfect celebration — from choosing a venue to making memories.
            </p>
          </div>

          {/* Steps */}
          <div className="bp-row">

            {/* connector spine (desktop) */}
            <div className="bp-spine" aria-hidden="true">
              <div className="bp-spine-line" />
              <div className="bp-spine-shimmer" />
            </div>

            {STEPS.map((s, i) => (
              <div key={i} className="bp-step">

                <div className="bp-circle-wrap">
                  <div className="bp-ring" />
                  <div className="bp-circle">
                    <span role="img" aria-label={s.title}>{s.icon}</span>
                    <div className="bp-num">{s.step}</div>
                  </div>
                </div>

                <div>
                  <h3 className="bp-step-title">{s.step}. {s.title}</h3>
                  <p className="bp-step-desc">{s.desc}</p>
                </div>

              </div>
            ))}

          </div>
        </div>
      </section>
    </>
  );
}